import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Reveals a group of text blocks as one continuous stream (ChatGPT-style).
 * Time-based, so long readings still finish in a predictable few seconds.
 * Respects prefers-reduced-motion and exposes a skip() action.
 */
export interface StreamedTextState {
  visible: number;
  done: boolean;
  skip: () => void;
}

export interface StreamSlice {
  parts: string[];
  /** Block currently receiving characters (-1 when finished). */
  active: number;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useStreamedText(
  texts: string[],
  enabled: boolean,
  speed = 260
): StreamedTextState {
  const signature = useMemo(() => texts.join('\u0000'), [texts]);
  const total = texts.reduce((sum, text) => sum + text.length, 0);
  const [visible, setVisible] = useState(() => (enabled ? 0 : total));
  const skipRef = useRef(false);

  useEffect(() => {
    if (!enabled || total === 0 || prefersReducedMotion()) {
      setVisible(total);
      return;
    }

    setVisible(0);
    skipRef.current = false;
    let raf = 0;
    const startedAt = performance.now();

    const tick = (now: number) => {
      if (skipRef.current) {
        setVisible(total);
        return;
      }
      const chars = Math.min(total, Math.floor(((now - startedAt) / 1000) * speed));
      setVisible(chars);
      if (chars < total) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [signature, enabled, total, speed]);

  return {
    visible,
    done: visible >= total,
    skip: () => {
      skipRef.current = true;
      setVisible(total);
    },
  };
}

/** Split the global reveal position into per-block substrings. */
export function sliceStream(texts: string[], visible: number): StreamSlice {
  let offset = 0;
  let active = -1;
  const parts = texts.map((text, index) => {
    const start = offset;
    const end = offset + text.length;
    offset = end;
    if (visible <= start) return '';
    if (visible <= end) {
      active = index;
      return text.slice(0, visible - start);
    }
    return text;
  });
  return { parts, active };
}
